<template>
    <div class="form">
        <section class="form-section">
            <div class="form-section-heading">
                {{ trans('clients.headline.client_information') }}
            </div>
            <div class="form-row">
                <div class="form-row__col form-row__col--one-half">
                    <form-input
                        name="name"
                        :required="true"
                        v-model="fields.name"
                        :error="validate('name')"
                    >
                        {{ trans('clients.label.name') }}
                    </form-input>
                </div>
            </div>

            <div class="form-row">
                <div class="form-row__col">
                    <form-input
                        name="street"
                        :required="true"
                        v-model="fields.street"
                        :error="validate('street')"
                    >
                        {{ trans('clients.label.street') }}
                    </form-input>
                </div>

                <div class="form-row__col">
                    <form-input
                        name="zip"
                        :required="true"
                        v-model="fields.zip"
                        :error="validate('zip')"
                    >
                        {{ trans('clients.label.zip') }}
                    </form-input>
                </div>
            </div>

            <div class="form-row">
                <div class="form-row__col">
                    <form-input
                        name="city"
                        :required="true"
                        v-model="fields.city"
                        :error="validate('city')"
                    >
                        {{ trans('clients.label.city') }}
                    </form-input>
                </div>

                <div class="form-row__col">
                    <label class="label label--required" for="country">{{
                        trans('clients.label.country')
                    }}</label>
                    <select
                        name="country"
                        v-model="fields.country"
                        :error="validate('country')"
                        :class="[
                            'form-select',
                            { 'form-select--error': errors('country') },
                        ]"
                    >
                        <option disabled>
                            {{ trans('clients.label.select') }}
                        </option>
                        <option
                            :value="country"
                            v-for="country in countries"
                            :key="country"
                        >
                            {{ trans('clients.country.' + country) }}
                        </option>
                    </select>
                    <span class="invalid-feedback" v-if="errors('country')">
                        <strong>{{ errors('country') }}</strong>
                    </span>
                </div>
            </div>

            <div class="form-row">
                <div class="form-row__col">
                    <checkbox
                        name="differing_billing_address"
                        v-model="fields.differingBillingAddress"
                    >
                        {{ trans('clients.label.differing_billing_address') }}
                    </checkbox>
                </div>
            </div>

            <div class="form-row" v-if="fields.differingBillingAddress">
                <div class="form-row__col">
                    <form-input
                        name="billing_street"
                        v-model="fields.billingStreet"
                        :error="validate('billing_street')"
                    >
                        {{ trans('clients.label.street') }}
                    </form-input>
                </div>
                <div class="form-row__col">
                    <form-input
                        name="billing_zip"
                        v-model="fields.billingZip"
                        :error="validate('billing_zip')"
                    >
                        {{ trans('clients.label.zip') }}
                    </form-input>
                </div>
            </div>

            <div class="form-row" v-if="fields.differingBillingAddress">
                <div class="form-row__col">
                    <form-input
                        name="billing_city"
                        v-model="fields.billingCity"
                        :error="validate('billing_city')"
                    >
                        {{ trans('clients.label.city') }}
                    </form-input>
                </div>
                <div class="form-row__col">
                    <label class="label" for="zip">{{
                        trans('clients.label.country')
                    }}</label>
                    <select
                        name="billing_country"
                        v-model="fields.billingCountry"
                        class="form-select"
                        :error="validate('billing_country')"
                    >
                        <option disabled>
                            {{ trans('clients.label.select') }}
                        </option>
                        <option
                            :value="country"
                            v-for="country in countries"
                            :key="country"
                        >
                            {{ trans('clients.country.' + country) }}
                        </option>
                    </select>
                </div>
            </div>
        </section>

        <section class="form-section" v-if="canCreateClientAdmin">
            <!-- <div class="form-row">
                <div class="form-row__col">
                    <checkbox
                        v-model="fields.createClientAdmin"
                        name="create_client_admin"
                    >
                        {{ trans('clients.label.create_client_admin') }}
                    </checkbox>
                </div>
            </div> -->

            <div class="form-row" v-if="fields.createClientAdmin">
                <div class="form-row__col">
                    <form-input
                        name="firstname"
                        :required="true"
                        v-model="fields.firstname"
                        :error="validate('firstname')"
                    >
                        {{ trans('clients.label.firstname') }}
                    </form-input>
                </div>
                <div class="form-row__col">
                    <form-input
                        name="lastname"
                        :required="true"
                        v-model="fields.lastname"
                        :error="validate('lastname')"
                    >
                        {{ trans('clients.label.lastname') }}
                    </form-input>
                </div>
            </div>
            <div class="form-row" v-if="fields.createClientAdmin">
                <div class="form-row__col form-row__col--one-half">
                    <form-input
                        name="email"
                        :required="true"
                        v-model="fields.email"
                        :error="validate('email')"
                    >
                        {{ trans('clients.label.email') }}
                    </form-input>
                </div>
            </div>
        </section>

        <section class="form-section">
            <div class="form-section-heading">
                {{ trans('clients.headline.additional_settings') }}
            </div>

            <div class="form-row">
                <div class="form-row__col form-row__col--one-half">
                    <label class="label" for="access_subclient">
                        {{ trans('clients.label.access_subclient') }}
                    </label>
                    <select
                        name="access_subclient"
                        class="form-select"
                        v-model="fields.accessSubclient"
                        id="access_sublient"
                    >
                        <option disabled>
                            {{ trans('clients.label.select') }}
                        </option>
                        <option :value="true">
                            {{ trans('buttons.yes') }}
                        </option>
                        <option :value="false">
                            {{ trans('buttons.no') }}
                        </option>
                    </select>
                </div>
                <div class="form-row__col form-row__col--one-half">
                    <form-input
                        name="overdue_delay"
                        :required="true"
                        type="number"
                        min="1"
                        step="1"
                        v-model="fields.overdueDelay"
                    >
                        {{ trans('clients.label.overdue_delay') }}
                    </form-input>
                </div>
            </div>

            <div class="form-row" v-if="isAdmin">
                <div class="form-row__col form-row__col--one-half">
                    <form-input
                        name="user_deletion_interval"
                        :required="true"
                        type="number"
                        min="1"
                        step="1"
                        v-model="fields.userDeletionInterval"
                    >
                        {{ trans('clients.label.user_deletion_interval') }}
                    </form-input>
                </div>
            </div>

            <div class="form-row">
                <div class="form-row__col">
                    <checkbox
                        name="static_next_assignment"
                        v-model="fields.staticNextAssignment"
                    >
                        {{ trans('clients.label.static_next_assignment') }}
                    </checkbox>
                </div>
            </div>
        </section>

        <section class="form-section">
            <div class="form-section-heading">
                {{ trans('clients.headline.specific_user_fields') }}
            </div>

            <extendable-card-list
                v-model="fields.specificUserFields"
                v-slot="slot"
                :cards="fields.specificUserFields"
                :new-card-object="{ name: '', required: 'false', key: 0 }"
                data-can-be-added
                data-can-be-deleted
                data-can-be-edited
            >
                <div class="form-row">
                    <div class="form-row__col">
                        <label class="label">{{
                            trans('clients.label.field_name')
                        }}</label>
                        <input
                            v-model="slot.data.name"
                            :class="[
                                'input',
                                {
                                    'input--error': errors(
                                        'specific_user_fields.' +
                                            slot.index +
                                            '.name'
                                    ),
                                },
                            ]"
                            type="text"
                            :name="
                                'specific_user_fields[' + slot.index + '][name]'
                            "
                        />
                        <small>{{
                            trans('clients.note.specific_fields')
                        }}</small>
                        <span
                            class="invalid-feedback"
                            v-if="
                                errors(
                                    'specific_user_fields.' +
                                        slot.index +
                                        '.name'
                                )
                            "
                        >
                            <strong>{{
                                errors(
                                    'specific_user_fields.' +
                                        slot.index +
                                        '.name'
                                )
                            }}</strong>
                        </span>
                    </div>
                    <div class="form-row__col">
                        <label class="label">{{
                            trans('clients.label.required_field')
                        }}</label>
                        <select
                            :class="[
                                'form-select',
                                {
                                    'form-select--error': errors(
                                        'specific_user_fields.' +
                                            slot.index +
                                            '.required'
                                    ),
                                },
                            ]"
                            :name="
                                'specific_user_fields[' +
                                    slot.index +
                                    '][required]'
                            "
                            v-model="slot.data.required"
                        >
                            <option value="true">
                                {{ trans('form.yes') }}
                            </option>
                            <option value="false">
                                {{ trans('form.no') }}
                            </option>
                        </select>
                        <span
                            class="invalid-feedback"
                            v-if="
                                errors(
                                    'specific_user_fields.' +
                                        slot.index +
                                        '.required'
                                )
                            "
                        >
                            <strong>{{
                                errors(
                                    'specific_user_fields.' +
                                        slot.index +
                                        '.required'
                                )
                            }}</strong>
                        </span>
                    </div>
                    <input
                        type="text"
                        :name="'specific_user_fields[' + slot.index + '][key]'"
                        :value="slot.data.key"
                        hidden
                    />
                </div>
            </extendable-card-list>
        </section>

        <section class="form-section" v-if="isAdmin">
            <div class="form-section-heading">
                {{ trans('clients.headline.api_settings') }}
            </div>

            <div
                class="form-row"
                v-if="client.api_token && fields.apiToken != client.api_token"
            >
                <div class="form-row__col">
                    <alert type="warning">
                        {{ trans('clients.note.api_token_changed') }}
                    </alert>
                </div>
            </div>

            <div class="form-row">
                <div class="form-row__col form-row__col--one-half">
                    <label class="label">
                        {{ trans('clients.label.api_token_activate') }}
                    </label>

                    <select
                        name="api_token_active"
                        :class="[
                            'form-select',
                            {
                                'form-select--error': errors(
                                    'api_token_active'
                                ),
                            },
                        ]"
                        v-model="fields.apiTokenExists"
                    >
                        <option value="1">{{ trans('form.yes') }}</option>
                        <option value="0">{{ trans('form.no') }}</option>
                    </select>
                </div>

                <div
                    class="form-row__col form-row__col--one-half"
                    v-if="fields.apiTokenExists == 1"
                >
                    <form-input
                        name="api_token"
                        :required="true"
                        type="text"
                        v-model="fields.apiToken"
                        :error="validate('api_token')"
                    >
                        {{ trans('clients.label.api_token') }}
                    </form-input>
                    <small>{{ trans('clients.label.api_token_note') }}</small>
                    <div class="h:margin-top-2">
                        <button
                            type="button"
                            @click="generateApiToken"
                            class="btn btn--primary"
                        >
                            {{ trans('clients.label.generate_api_token') }}
                        </button>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import { EventBus } from '../eventbus';

export default {
    model: {
        prop: 'clientFormFields',
        event: 'bubbleClientFormFields',
    },
    props: {
        client: {
            type: Object,
            default: () => {
                return {};
            },
        },
        formerrors: {
            type: Object,
            default: () => {
                return {};
            },
        },
        parent_id: {
            type: Number,
        },
        canCreateClientAdmin: {
            type: Boolean,
        },
        isAdmin: {
            type: Boolean,
            default: false,
        },
        tenantIdent: {
            type: String,
        },
    },

    data() {
        return {
            fields: {
                name: this.old('name', this.client.name),
                street: this.old('street', this.client.street),
                zip: this.old('zip', this.client.zip),
                city: this.old('city', this.client.city),
                country: this.old('country', this.client.country || 'Germany'),
                differingBillingAddress: !!this.old(
                    'differing_billing_address',
                    this.client.differing_billing_address
                ),
                billingStreet: this.old(
                    'billing_street',
                    this.client.billing_street
                ),
                billingZip: this.old('billing_zip', this.client.billing_zip),
                billingCity: this.old('billing_city', this.client.billing_city),
                billingCountry: this.old(
                    'billing_country',
                    this.client.billing_country
                ),
                userDeletionInterval: this.old(
                    'user_deletion_interval',
                    this.client.user_deletion_interval || 4
                ),
                overdueDelay: this.old(
                    'overdue_delay',
                    this.client.overdue_delay || 6
                ),
                staticNextAssignment: this.old(
                    'static_next_assignment',
                    this.client.static_next_assignment || false
                ),
                accessSubclient: !!this.old(
                    'access_subclient',
                    this.client.access_subclient || true
                ),
                specificUserFields: this.old(
                    'specific_user_fields',
                    this.client.specific_user_fields
                        ? this.client.specific_user_fields.data ||
                              this.client.specific_user_fields
                        : []
                ),
                createClientAdmin: !!this.old('create_client_admin', false),
                firstname: this.old('firstname', this.client.firstname),
                lastname: this.old('lastname', this.client.lastname),
                email: this.old('email', this.client.email),
                apiTokenExists: this.old(
                    'api_token_active',
                    this.client.api_token != null ? 1 : 0
                ),
                apiToken: this.old('api_token', this.client.api_token),
            },
            countries: [
                'Germany',
                'Belgium',
                'Bulgaria',
                'Denmark',
                'Estonia',
                'Finland',
                'France',
                'Greece',
                'Ireland',
                'Italy',
                'Croatia',
                'Latvia',
                'Lithuania',
                'Luxembourg',
                'Malta',
                'Netherlands',
                'Austria',
                'Poland',
                'Portugal',
                'Romania',
                'Slovakia',
                'Slovenia',
                'Spain',
                'Sweden',
                'Czech Republic',
                'Hungary',
                'United Kingdom',
                'Cyprus',
            ],
            back_url: this.parent_id
                ? this.route('tenant.clients.show', this.parent_id)
                : this.route('tenant.clients.index'),
        };
    },

    methods: {
        generateApiToken() {
            var chars =
                'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
            var token = '';
            for (var i = 0; i < 60; i++) {
                token += chars[Math.floor(Math.random() * chars.length)];
            }

            this.fields.apiToken = this.tenantIdent + '-' + token;
        },

        validate(attribute) {
            return this.formerrors[attribute]
                ? this.formerrors[attribute][0]
                : '';
        },
    },

    watch: {
        fields: {
            handler() {
                this.$emit('bubbleClientFormFields', this.fields);
            },
            deep: true,
        },
    },
};
</script>
